<template>
  <div class="general-tab py-1">
    <b-row class="mb-3 no-internal-print">
      <b-col>
        <life-expectancy-modal
          class="float-right"
          :windingsAmount="tabInfo.windingAmount"
          @life-expectancy-saved="refreshData()"
        />
      </b-col>
    </b-row>
    <div class="general-tab__criteria-area">
      <label for="criteria">
        {{ $t('engineering.isolation-aging.end-life-criteria') }}
      </label>
      <s-select
        id="criteria"
        v-model="selectedCriteria"
        value-field="id"
        text-field="name"
        :disabled="loadingCriteria"
        :options="criteria"
        @input="onChangeCriteria"
      >
        <div
          slot="item"
          slot-scope="data"
        >
          <i
            v-show="data.alarmed"
            class="fas fa-exclamation-triangle"
            aria-hidden="true"
          />
          <span>{{ data.name }}</span>
        </div>
      </s-select>
      <div class="general-tab__windings">
        <div
          v-for="index in tabInfo.windingAmount"
          class="general-tab__winding avoid-break"
          :key="index"
        >
          <winding-section
            :data="windingsData[index]"
            :loading="windingLoadings[index]"
            :winding-id="index"
            :installation-time-zone="tabInfo.installationTimeZone"
          />
        </div>
      </div>
      <div class="general-tab__critic-hours-wrapper avoid-break">
        <critical-hour-distribution-chart
          :chart-data="chartData"
          :occurrences="tabInfo.criticalTimeOccurrenceLimit"
          :loading="loadingCriticalHour"
          :installation-time-zone="tabInfo.installationTimeZone"
        />
      </div>
    </div>
    <div class="general-tab__criterion-comparative-table avoid-break">
      <criterion-comparative-table
        :table-items="tableItems"
        :loading-table="loadingTable"
        :winding-number="tabInfo.windingAmount"
      />
    </div>
  </div>
</template>

<script>
import eventBus from '@/services/globalEventBus';
import events from '@/domain/events/engineering';
import LifeExpectancyModal
  from '@/domain/components/installation/engineering/isolationAging/modal/LifeExpectancyModal';
import isolationEvents from '@/domain/events/isolationAging';
import Service from '@/domain/services/installation/engineering/isolationAging/isolationAgingService';
import CriticalHourDistributionChart from '@/domain/components/installation/engineering/isolationAging/CriticalHourDistributionChart';
import CriterionComparativeTable from '@/domain/components/installation/engineering/isolationAging/CriterionComparativeTable';
import WindingSection from '@/domain/components/installation/engineering/isolationAging/windingSection/WindingSection';

export default {
  name: 'IsolationAgingGeneralTab',
  props: {
    tabInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    LifeExpectancyModal,
    CriticalHourDistributionChart,
    CriterionComparativeTable,
    WindingSection,
  },
  data: () => ({
    showLifeExpectancyModal: false,
    criteria: [],
    chartData: [],
    tableItems: [],
    windingsData: [],
    loadingCriteria: false,
    loadingCriticalHour: false,
    loadingTable: false,
    windingLoadings: [],
  }),
  computed: {
    selectedCriteria: {
      get() {
        return this.tabInfo.selectedCriteria;
      },
      set(value) {
        eventBus.emit(isolationEvents.refreshCriteria, value);
      },
    },
  },
  created() {
    this.initListener();
    this.refreshData(true);
  },
  beforeDestroy() {
    this.removeListener();
  },
  methods: {
    initListener() {
      eventBus.on(events.refreshDataTab, this.refreshData, this);
    },
    removeListener() {
      eventBus.off(events.refreshDataTab, this.refreshData, this);
    },
    onChangeCriteria() {
      this.$nextTick(() => {
        this.getWindingsDataByWindingAmount(true);
        this.getChartData(true);
      });
    },
    getCriteria(showLoading) {
      this.loadingCriteria = showLoading;
      Service.getCriterion(this.$route.params.assetModuleId).then((response) => {
        this.criteria = response;
      }).finally(() => {
        this.loadingCriteria = false;
      });
    },
    getChartData(showLoading) {
      this.loadingCriticalHour = showLoading;
      Service.getCriticalHourDistribution(
        this.$route.params.assetModuleId,
        this.selectedCriteria,
      ).then((response) => {
        this.chartData = response;
      }).finally(() => {
        this.loadingCriticalHour = false;
      });
    },
    getTableData(showLoading) {
      this.loadingTable = showLoading;
      Service.getWindingRemainingLifeByCriterion(
        this.$route.params.assetModuleId,
      ).then((response) => {
        this.tableItems = response;
      }).finally(() => {
        this.loadingTable = false;
      });
    },
    getWindingData(showLoading, windingId) {
      const { assetModuleId } = this.$route.params;
      this.windingLoadings[windingId] = showLoading;
      Service.getWindingById(assetModuleId, windingId, this.selectedCriteria)
        .then((response) => {
          this.windingsData[windingId] = response;
        })
        .finally(() => {
          this.$set(this.windingLoadings, windingId.toString(), false);
        });
    },
    getWindingsDataByWindingAmount(showLoading) {
      if (!this.tabInfo.windingAmount) return;
      for (let windingId = 1; windingId <= this.tabInfo.windingAmount; windingId += 1) {
        this.getWindingData(showLoading, windingId);
      }
    },
    refreshData(showLoading) {
      if (!this.tabInfo.windingAmount) return;
      this.getCriteria(showLoading);
      this.getWindingsDataByWindingAmount(showLoading);
      this.getChartData(showLoading);
      this.getTableData(showLoading);
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"

.general-tab
  padding: 0
  &__criteria-area
    border: 1px solid $color-border-gray
    border-radius: 3px
    padding: 10px
  &__criterion-comparative-table
    margin-top: 15px
    border: 1px solid $color-border-gray
    border-radius: 3px
    padding: 10px 10px 0
  ::v-deep
    .dropdown-item i
      color: var(--color-danger)
      font-size: 13px
    .dropdown-item:hover i
      color: white
</style>
